// Body.jsx
// DL 11/11/2k22

// Packages, Dependencies, and Shared Libraries
import React from "react";

// React Components
import CareBubble from "./Care/CareBubble";
import CareContainer from "./Care/CareDetails";

// CSS
import { useState, useEffect, useRef } from "react";

/*******************************************************************************
 * @func Body (React Component)
 * The body of the Bio Page. Is a parent to many other Plant Bio components.
 *
 * @param plant (object): The plant object that contains all the information
 * about our little, leafy friend queried from the DB
 * @param species (object): The shared species object queried from the DB
 * @param plantID: (string): The unique document ID of the plant
 * @param onLoadCallback (function): Callback function that fires whenever this
 * component loads. Used to tell the caller that this has finished rendering
 * and is ready to show.
 * @param scale (numeric): Scaling factor passed onto the PlantImage
 * subcomponent
 *
 * @notes plantID is currently not used because it is for the reporting features
 * which have been disabled for now
 *
 ******************************************************************************/
const Body = ({ plant, species, plantID, sharedSprite }) => {
  const normalizedPlantName =
    typeof plant?.name === "string" ? plant.name.trim() : "";
  const ownerId = plant?.ownedBy || plant?.owner || "";
  const isOwned = Boolean(ownerId);
  const hasCustomName = Boolean(normalizedPlantName);
  const displayName =
    normalizedPlantName || species?.commonName || "this plant";
  const environmentNotes =
    typeof plant?.environmentNotes === "string"
      ? plant.environmentNotes.trim()
      : "";
  const userNotes =
    typeof plant?.userNotes === "string" ? plant.userNotes.trim() : "";
  const shouldForceGeneral = !hasCustomName && !isOwned;
  const defaultCareView = shouldForceGeneral ? "general" : "thisPlant";

  // Hooks
  const [careBubbles, setCareBubbles] = useState([]);
  const [careView, setCareView] = useState(defaultCareView);
  const careContainerRef = useRef(null);

  // Function to scroll to care information section
  const scrollToCareInfo = () => {
    if (careView !== "general") {
      setCareView("general");
      setTimeout(() => {
        careContainerRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }, 120);
      return;
    }

    careContainerRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  // This fires when the species object updates to create the care instruction
  // bio bubbles
  useEffect(() => {
    setCareBubbles([
      {
        instruction: species.careInstructions.waterRating,
        image: "water",
      },
      {
        instruction: species.careInstructions.lightRating,
        image: "light",
      },
      {
        instruction: species.careInstructions.difficulty,
        image: "plusIcon",
      },
    ]);
  }, [species]); // End useEffect

  useEffect(() => {
    if (shouldForceGeneral) {
      setCareView("general");
    }
  }, [shouldForceGeneral]);

  return (
    <React.Fragment>
      <div className="pl-5 pt-2 text-leaf-green-dark text-[21px] flex items-center justify-start">
        <span
          className="font-header font-normal -mb-1 -rotate-1"
          style={{ textShadow: "0 1px 0 rgba(255,255,255,0.7)" }}
        >
          Say "Hello!" to...
        </span>
      </div>
      <div className="animate-fade-in p-3 pb-2 rounded-3xl bg-body-bg text-left mx-1 mb-1 shadow-card border border-leaf-brown/10">
        <div className="flex flex-wrap justify-center mx-auto">
          <div className="relative flex-1 min-w-[220px] flex justify-center">
            <div
              className="absolute top-0 left-2 right-2 sm:left-2 sm:right-6 text-leaf-green-dark font-global max-w-[360px] z-10"
              style={{
                textShadow: "0 1px 2px rgba(255,255,255,0.8)",
              }}
            >
              <div className="text-[36px] leading-tight font-extrabold">
                {displayName}
              </div>
              {hasCustomName && species?.commonName && (
                <div className="text-base font-semibold text-leaf-brown-soft">
                  {species.commonName}
                </div>
              )}
            </div>
            {/* The sprite bottom-aligns with the care bubble column (the
                sprite wrapper stretches to the row height, and the sprite
                itself is a justify-end column) */}
            <div className="flex justify-center w-full">{sharedSprite}</div>
            {/* <div
              className="absolute left-2 bottom-0 z-20 -mb-6 flex items-center gap-1"
              // style={{ maxWidth: "220px" }}
            >
              <span className="text-[11px] uppercase tracking-wide text-green-800 font-bold">
                Next water due:
              </span>
              <button
                type="button"
                className="text-[14px]"
                onClick={handleWaterLink}
                // className="rounded-[999px] px-4 py-1 bg-green-700 text-white border-1 border-white font-semibold text-sm shadow-md hover:bg-green-600 transition-colors text-center"
              >
                Track in the Leaflings app ➡️
              </button>
            </div> */}
          </div>
          <div className="flex flex-col items-center font-global gap-2 justify-end min-h-[230px]">
            {careBubbles.map((data, index) => {
              return (
                <CareBubble
                  key={"bioBubble" + String(index + 1)}
                  data={data}
                  onClick={scrollToCareInfo}
                ></CareBubble>
              );
            })}
          </div>
        </div>

        <div ref={careContainerRef} className="mt-4">
          <CareContainer
            careInstructions={species.careInstructions}
            species={plant.species}
            imageName={species.stages?.[0]?.plantBody?.imageName || ""}
            careView={careView}
            onChangeCareView={setCareView}
            isOwned={isOwned}
            environmentNotes={environmentNotes}
            userNotes={userNotes}
          />
        </div>

        {/*<p className="contentSectionHeader">About me</p>  */}
        {/* <Bio plant={plant} />
        <div className="Gpt3Message">{OPENAI_CREDIT}</div> */}
        {/* Leaving Out Rating scale for now, until we want to use it
          <RatingScale plantID={plantID} plantName={plant.getName()}></RatingScale>*/}
      </div>
    </React.Fragment>
  );
}; // End PlantBio

export default Body;
